import { useMemo } from "react";
import * as Diff from "diff";

/**
 * DiffView — side-by-side line diff between `before` and `after` markdown.
 * Uses jsdiff's diffLines under the hood; renders red rows for removed
 * lines and green rows for added lines. Common lines appear in both
 * columns; add-only rows show blank on the left, remove-only on the right.
 */
export function DiffView({ before = "", after = "" }) {
  const rows = useMemo(() => computeRows(before, after), [before, after]);

  const addedCount = rows.filter((r) => r.type === "added").length;
  const removedCount = rows.filter((r) => r.type === "removed").length;
  const commonCount = rows.filter((r) => r.type === "same").length;

  return (
    <div className="rounded-md border border-white/10 bg-black/40 overflow-hidden text-[11px] font-mono">
      <div className="flex items-center gap-3 px-3 py-1.5 bg-white/[0.03] border-b border-white/5 text-[10px]"
           data-testid="diff-summary">
        <span className="text-emerald-300">+{addedCount}</span>
        <span className="text-rose-300">−{removedCount}</span>
        <span className="text-zinc-500 ml-auto">{commonCount} unchanged</span>
      </div>
      <div className="grid grid-cols-2 divide-x divide-white/5 max-h-[380px] overflow-y-auto">
        <div className="min-w-0" data-testid="diff-before">
          <div className="sticky top-0 bg-black/60 backdrop-blur px-2 py-1 text-[9px] uppercase tracking-widest text-zinc-500 border-b border-white/5">
            Current
          </div>
          {rows.map((r, i) => (
            <div key={`b-${i}`}
                 data-testid={`diff-line-before-${i}`}
                 className={
                   "px-2 py-0.5 whitespace-pre-wrap break-words leading-snug " +
                   (r.type === "removed" ? "bg-rose-500/[0.10] text-rose-100" :
                    r.type === "added" ? "opacity-30" :
                    "text-zinc-300")
                 }>
              {r.before || <span className="opacity-30">·</span>}
            </div>
          ))}
        </div>
        <div className="min-w-0" data-testid="diff-after">
          <div className="sticky top-0 bg-black/60 backdrop-blur px-2 py-1 text-[9px] uppercase tracking-widest text-zinc-500 border-b border-white/5">
            After
          </div>
          {rows.map((r, i) => (
            <div key={`a-${i}`}
                 data-testid={`diff-line-after-${i}`}
                 className={
                   "px-2 py-0.5 whitespace-pre-wrap break-words leading-snug " +
                   (r.type === "added" ? "bg-emerald-500/[0.10] text-emerald-100" :
                    r.type === "removed" ? "opacity-30" :
                    "text-zinc-300")
                 }>
              {r.after || <span className="opacity-30">·</span>}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function computeRows(before, after) {
  const parts = Diff.diffLines(before || "", after || "", { newlineIsToken: false });
  const rows = [];
  let i = 0;
  while (i < parts.length) {
    const cur = parts[i];
    const next = parts[i + 1];
    const linesOf = (v) => (v || "").replace(/\n$/, "").split("\n");
    // Pair a removed chunk immediately followed by an added chunk so
    // they show up on the same row instead of stacking.
    if (cur.removed && next?.added) {
      const beforeLines = linesOf(cur.value);
      const afterLines = linesOf(next.value);
      const maxLen = Math.max(beforeLines.length, afterLines.length);
      for (let k = 0; k < maxLen; k++) {
        rows.push({
          type: k < beforeLines.length && k < afterLines.length
                  ? "modified"
                  : k < beforeLines.length ? "removed" : "added",
          before: beforeLines[k] ?? "",
          after: afterLines[k] ?? "",
        });
      }
      i += 2;
      continue;
    }
    for (const line of linesOf(cur.value)) {
      if (cur.added) rows.push({ type: "added", before: "", after: line });
      else if (cur.removed) rows.push({ type: "removed", before: line, after: "" });
      else rows.push({ type: "same", before: line, after: line });
    }
    i += 1;
  }
  return rows;
}

export default DiffView;
